/**
 * LegalScheduleTab — per-case legal schedule inside the Customer File
 * (legal mode). Replaces the old "Coming in Phase 2" placeholder.
 *
 *   • Issuing Court card — select & log the court the matter is issued at
 *   • The case's legal calendar events with traffic-light priorities
 *     (server-computed from Portal Settings → Legal Diary thresholds)
 *   • Add event — shared dialog with diary hard-limits + court attachment
 */
import { useState, useEffect, useCallback } from "react";
import { ukDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import {
  Scale, Plus, Loader2, CheckCircle2, Ban, Trash2,
  Landmark, Calendar as CalIcon, Lock, CalendarClock,
} from "lucide-react";
import RediariseDialog from "./RediariseDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  listLegalEvents, listLegalEventTypes, updateLegalEvent, deleteLegalEvent,
  getLegalDiarySettings, listLegalStages,
} from "@/lib/api";
import IssuingCourtCard from "@/pages/legal/IssuingCourtCard";
import LegalStageLadder from "@/pages/legal/LegalStageLadder";
import { useAuth } from "@/contexts/AuthContext";
import AddLegalEventDialog from "@/pages/legal/AddLegalEventDialog";
import { OwnerChip } from "@/pages/legal/OwnerChip";
import { TONE_CLASS, STATUS_TONE, PriorityDot, courtSummary } from "@/pages/legal/legalPriority";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export default function LegalScheduleTab({ caseRow, onChanged }) {
  const navigate = useNavigate();
  const [events, setEvents]           = useState([]);
  const [eventTypes, setEventTypes]   = useState([]);
  const [stages, setStages]           = useState([]);
  const [diarySettings, setDiarySettings] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [addOpen, setAddOpen]         = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, s, st] = await Promise.all([listLegalEventTypes(), getLegalDiarySettings(), listLegalStages()]);
        setEventTypes(t.types || []);
        setDiarySettings(s);
        setStages(st.stages || []);
      } catch (e) { console.warn("legal schedule bootstrap failed", e); }
    })();
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listLegalEvents({ case_id: caseRow.case_id, include_notes: false, limit: 500 });
      setEvents(r.events || []);
    } catch (e) {
      toast.error("Failed to load legal schedule: " + errorDetailMessage(e, e.message));
    } finally {
      setLoading(false);
    }
  }, [caseRow.case_id]);
  useEffect(() => { reload(); }, [reload]);

  const scheduled = events.filter(e => e.status === "scheduled");
  const history   = events.filter(e => e.status !== "scheduled")
                          .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px,1fr] gap-4" data-testid="legal-schedule-tab">
      <div className="space-y-4">
        <LegalStageLadder stages={stages} caseRow={caseRow} />
        <IssuingCourtCard caseRow={caseRow} onChanged={onChanged} />
        <Button
          variant="outline"
          size="sm"
          className="w-full h-8 text-xs"
          onClick={() => navigate("/legal/calendar")}
          data-testid="legal-schedule-open-calendar"
        >
          <CalIcon className="w-3.5 h-3.5 mr-1.5" /> Open Legal Calendar (all cases)
        </Button>
      </div>

      <Card data-testid="legal-schedule-card">
        <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Scale className="w-4 h-4 text-red-700" />
            Legal Schedule
            {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </CardTitle>
          <Button
            size="sm"
            className="h-7 text-xs bg-red-700 hover:bg-red-800"
            onClick={() => setAddOpen(true)}
            data-testid="legal-schedule-add-event"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add event
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {events.length === 0 && !loading ? (
            <div className="p-8 text-center" data-testid="legal-schedule-empty">
              <CalendarClock className="w-8 h-8 mx-auto text-red-200" />
              <div className="mt-2 text-sm font-medium text-slate-800">Nothing in the diary for this file yet</div>
              <div className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                Every live legal file needs a next action — it is what shows up in My Actions and the Agenda.
              </div>
              <Button size="sm" className="mt-4 h-8 text-xs bg-red-700 hover:bg-red-800" onClick={() => setAddOpen(true)} data-testid="legal-schedule-add-first-action">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add the first action
              </Button>
            </div>
          ) : (
            <>
              {scheduled.length === 0 && !loading && (
                <div className="px-4 py-3 border-b bg-amber-50/60 flex items-center gap-3 text-xs" data-testid="legal-schedule-no-open-action">
                  <CalendarClock className="w-4 h-4 text-amber-700 shrink-0" />
                  <span className="text-amber-900 flex-1">No open action on this file — add the next step so it stays on the Agenda.</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300 bg-white" onClick={() => setAddOpen(true)} data-testid="legal-schedule-add-next-action">
                    <Plus className="w-3 h-3 mr-1" /> Add next action
                  </Button>
                </div>
              )}
              {scheduled.length > 0 && (
                <ul className="divide-y" data-testid="legal-schedule-scheduled">
                  {scheduled.map(ev => (
                    <ScheduleRow key={ev.event_id} ev={ev} onChanged={reload} />
                  ))}
                </ul>
              )}
              {history.length > 0 && (
                <>
                  <div className="px-4 py-1.5 bg-slate-50 border-y text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    History ({history.length})
                  </div>
                  <ul className="divide-y opacity-75" data-testid="legal-schedule-history">
                    {history.map(ev => (
                      <ScheduleRow key={ev.event_id} ev={ev} onChanged={reload} readonly />
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AddLegalEventDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventTypes={eventTypes}
        defaultDate={new Date().toISOString().slice(0, 10)}
        diarySettings={diarySettings}
        caseCourts={caseRow.issuing_courts || null}
        fixedCase={{
          case_id: caseRow.case_id,
          customer_name: caseRow.customer_name,
          loan_application_id: caseRow.loan_application_id,
        }}
        onCreated={() => { setAddOpen(false); reload(); }}
      />
    </div>
  );
}

function ScheduleRow({ ev, onChanged, readonly = false }) {
  const [busy, setBusy] = useState(false);
  const [rediarise, setRediarise] = useState(false);
  const { isAdmin } = useAuth();
  const lockedForMe = !!ev.locked && !isAdmin();
  const setStatus = async (newStatus) => {
    try {
      setBusy(true);
      await updateLegalEvent(ev.event_id, { status: newStatus, actor_is_admin: isAdmin() });
      toast.success(`Marked ${newStatus}`);
      await onChanged?.();
    } catch (e) {
      toast.error("Update failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${ev.label}" on ${ukDate(ev.date)}?`)) return;
    try {
      setBusy(true);
      await deleteLegalEvent(ev.event_id, false, isAdmin());
      toast.success("Event deleted");
      await onChanged?.();
    } catch (e) {
      toast.error("Delete failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };

  return (
    <li className="px-4 py-2.5 hover:bg-slate-50" data-testid={`legal-schedule-row-${ev.event_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <PriorityDot priority={ev.priority} daysOverdue={ev.days_overdue} />
        <span className="text-xs font-mono text-slate-600 tabular-nums">{ukDate(ev.date)}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${TONE_CLASS[ev.tone] || TONE_CLASS.slate}`}>
          {ev.label}
        </span>
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[ev.status]}`}>
          {ev.status}
        </span>
        {(ev.completed_at || ev.cancelled_at) && (
          <span className="text-[10px] text-slate-500" data-testid={`legal-audit-stamp-${ev.event_id}`}>
            {ev.status === "cancelled" ? "cancelled" : "completed"} {new Date(ev.cancelled_at || ev.completed_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} by {ev.cancelled_by || ev.completed_by || "—"}
          </span>
        )}
        {ev.locked && (
          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-300 inline-flex items-center gap-0.5"
            title="Hearing-prep task — locked to the hearing date. Complete it; only an admin can move or remove it."
            data-testid={`legal-locked-badge-${ev.event_id}`}>
            <Lock className="w-2.5 h-2.5" /> locked
          </span>
        )}
        {ev.mandatory && !ev.locked && (
          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200" title="Mandatory — can only be completed, not cancelled" data-testid={`legal-mandatory-badge-${ev.event_id}`}>mandatory</span>
        )}
        {ev.sla_breached_at && ev.status === "scheduled" && (
          <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-rose-600 text-white border-rose-700" title={`SLA deadline ${ukDate(ev.sla_deadline)} passed — ${ev.sla_days_late ?? 0} working day(s) late`} data-testid={`legal-sla-badge-${ev.event_id}`}>SLA breached</span>
        )}
        <OwnerChip ev={ev} onChanged={onChanged} />
        {ev.priority && ev.days_overdue > 0 && (
          <span className="text-[10px] text-red-700 font-semibold">
            {ev.days_overdue}d overdue
          </span>
        )}
        {ev.auto_followup_of && (
          <span className="text-[9px] text-slate-400" title="Auto-created follow-up">⤴︎ auto</span>
        )}
        {!readonly && (
          <span className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5" onClick={() => setStatus("completed")} disabled={busy} title="Mark complete">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            </Button>
            {!lockedForMe && (
              <>
                {ev.status === "scheduled" && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5" onClick={() => setRediarise(true)} disabled={busy} title="Re-diarise (reason required)" data-testid={`sched-rediarise-${ev.event_id}`}>
                    <CalendarClock className="w-3 h-3 text-sky-700" />
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-6 text-[10px] px-1.5" onClick={() => setStatus("cancelled")} disabled={busy || !!ev.mandatory} title={ev.mandatory ? "Mandatory action — complete it instead" : "Cancel"}>
                  <Ban className="w-3 h-3 text-slate-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-rose-600" onClick={remove} disabled={busy} title="Delete">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </span>
        )}
      </div>
      {ev.court?.name && (
        <div className="text-[10px] text-slate-600 mt-1 flex items-center gap-1 pl-4">
          <Landmark className="w-2.5 h-2.5" /> {courtSummary(ev.court)}
        </div>
      )}
      {ev.notes && <div className="text-[11px] text-slate-600 mt-0.5 italic pl-4">&ldquo;{ev.notes}&rdquo;</div>}
      {ev.schedule_override?.justification && (
        <div className="text-[10px] text-amber-800 mt-0.5 pl-4" title="Scheduled beyond the diary limit">
          ⚠ Diary override: {ev.schedule_override.justification}
        </div>
      )}
      {ev.lock_override?.justification && (
        <div className="text-[10px] text-amber-800 mt-0.5 pl-4" title="Locked task moved by an admin">
          ⚠ Admin moved this locked task: {ev.lock_override.justification}
        </div>
      )}
      {rediarise && <RediariseDialog ev={ev} onClose={() => setRediarise(false)} onDone={async () => { setRediarise(false); await onChanged?.(); }} />}
    </li>
  );
}
